import axios from "axios"
import SnackbarComponent from "components/SnackBar"
import config from "config/config"
import { convertToRaw, EditorState, ContentState } from "draft-js"
import draftToHtml from "draftjs-to-html"
import React, { useState } from "react"
import { Editor } from "react-draft-wysiwyg"
import { useSelector } from "react-redux"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import convertMarkdownToUnicode from "markdown-to-unicode"
import turndown from "turndown"
import "./index.css"

const CreatePost = () => {
  const turndownService = new turndown()
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const mode = useSelector(state => state.Layout.layoutMode)
  const userData = useSelector(state => state.Login.userData)

  const [postContent, setPostContent] = useState("")
  const [postNow, setPostNow] = useState(true)
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [images, setImages] = useState([])
  const [imagePreviews, setImagePreviews] = useState([])
  const [success, setSuccess] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)
  const [scheduleModal, setScheduleModal] = useState(false)
  const [scheduleDate, setScheduleDate] = useState("")
  const [postToFacebook, setPostToFacebook] = useState(false)
  const [suggestModalOpen, setSuggestModalOpen] = useState(false)
  const [captionTopic, setCaptionTopic] = useState("")
  const [captionSuggestions, setCaptionSuggestions] = useState([])
  const [captionLoading, setCaptionLoading] = useState(false)
  // console.log(
  //   activeOrganization.InstagramIntegrations[0].pageId,
  //   "pageId",
  //   activeOrganization.InstagramIntegrations[0].pageToken,
  //   "pageToken",
  // )

  const toggleScheduleModal = () => setScheduleModal(!scheduleModal)
  const toggleSuggestModal = () => setSuggestModalOpen(!suggestModalOpen)

  const onEditorStateChange = editorState => {
    setEditorState(editorState)

    const rawHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()))
    const markdown = turndownService.turndown(rawHtml)
    const unicodeText = convertMarkdownToUnicode(markdown)

    setPostContent(unicodeText)
  }

  // AI post caption drafting: fetches 1-3 draft captions, admin picks one
  // (or none) and can still edit it in the editor before posting/scheduling.
  // Does not touch the publish/schedule flow itself.
  const suggestCaptions = async () => {
    setCaptionLoading(true)
    try {
      const response = await axios.post(
        `${config.backendUrl}/ai/generate-post-caption`,
        {
          organizationId: activeOrganization.id,
          platform: "instagram",
          topic: captionTopic || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )
      setCaptionSuggestions(response.data.captions || [])
    } catch (err) {
      setErr("Error generating AI captions. You can still write your own.")
    } finally {
      setCaptionLoading(false)
    }
  }

  const applyCaption = caption => {
    const newEditorState = EditorState.createWithContent(
      ContentState.createFromText(caption),
    )
    onEditorStateChange(newEditorState)
    setSuggestModalOpen(false)
  }

  const handleImageChange = e => {
    const files = Array.from(e.target.files)
    setImages(prevImages => [...prevImages, ...files])

    const newImagePreviews = files.map(file => URL.createObjectURL(file))
    setImagePreviews(prevPreviews => [...prevPreviews, ...newImagePreviews])
  }

  const removeImage = index => {
    const updatedImages = images.filter((_, i) => i !== index)
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index)
    setImages(updatedImages)
    setImagePreviews(updatedPreviews)
  }

  const handlePostCreation = async () => {
    if (
      !activeOrganization?.InstagramIntegrations[0]?.pageId ||
      !activeOrganization?.InstagramIntegrations[0]?.pageToken
    ) {
      setErr("Instagram integration is missing. Please check your settings.")
      return
    }

    setLoading(true)
    setErr(null)
    setSuccess(null)

    try {
      // Step 1: Upload all selected images/videos to S3
      const s3UploadPromises = images.map(async file => {
        const formData = new FormData()
        formData.append("file", file)

        const res = await axios.post(
          `${config.backendUrl}/aws/file?email=reputation-management`,
          formData,
        )
        return res.data.url // Public S3 URL
      })

      const s3Urls = await Promise.all(s3UploadPromises)

      // Step 2: Upload each S3 URL to Instagram (unpublished media)
      const mediaContainerIds = []

      for (const url of s3Urls) {
        const uploadResponse = await axios.post(
          `https://graph.facebook.com/v20.0/${activeOrganization.InstagramIntegrations[0].pageId}/media`,
          {
            image_url: url,
            caption: postContent,
            access_token: activeOrganization.InstagramIntegrations[0].pageToken,
          },
        )

        if (!uploadResponse.data.id) {
          throw new Error("Failed to upload media to Instagram.")
        }

        mediaContainerIds.push(uploadResponse.data.id)
      }

      // Step 3: Create carousel or single post
      let creationId
      if (mediaContainerIds.length > 1) {
        const carouselResponse = await axios.post(
          `https://graph.facebook.com/v20.0/${activeOrganization.InstagramIntegrations[0].pageId}/media`,
          {
            media_type: "CAROUSEL",
            caption: postContent,
            children: mediaContainerIds,
            access_token: activeOrganization.InstagramIntegrations[0].pageToken,
          },
        )
        creationId = carouselResponse.data.id
      } else {
        creationId = mediaContainerIds[0]
      }

      // Step 4: Publish post
      await axios.post(
        `https://graph.facebook.com/v20.0/${activeOrganization.InstagramIntegrations[0].pageId}/media_publish`,
        {
          creation_id: creationId,
          access_token: activeOrganization.InstagramIntegrations[0].pageToken,
        },
      )

      setSuccess("🎉 Instagram post published successfully!")

      // Step 5: Optionally post to Facebook
      if (postToFacebook) {
        try {
          let attachedMedia = s3Urls.map(url => ({ media_url: url }))
          await axios.post(
            `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/feed`,
            {
              message: postContent,
              attached_media: attachedMedia,
              access_token:
                activeOrganization.FacebookIntegrations[0].pageToken,
            },
          )
          setSuccess("🎉 Post also published on Facebook!")
        } catch (error) {
          console.error("Error posting to Facebook:", error)
          setErr("Failed to post on Facebook.")
        }
      }

      // Reset state
      setPostContent("")
      setEditorState(EditorState.createEmpty())
      setImages([])
      setImagePreviews([])
    } catch (error) {
      console.error("Error creating Instagram post:", error)
      setErr("Error creating post. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const schedulePost = async () => {
    setLoading(true)
    setErr(null)
    setSuccess(null)

    try {
      const scheduleTimestamp = Math.floor(
        new Date(scheduleDate).getTime() / 1000,
      )
      const minAllowedTime = Math.floor(Date.now() / 1000) + 600

      if (scheduleTimestamp < minAllowedTime) {
        setErr("Scheduled time must be at least 10 minutes in the future.")
        setLoading(false)
        return
      }

      // Step 1: Upload all media files to S3
      const s3UploadPromises = images.map(async file => {
        const formData = new FormData()
        formData.append("file", file)

        const res = await axios.post(
          `${config.backendUrl}/aws/file?email=reputation-management`,
          formData,
        )

        return res.data.url // Public S3 URL
      })

      const s3Urls = await Promise.all(s3UploadPromises)

      // Step 2: Prepare data for backend scheduling API
      const formData = {
        message: postContent,
        scheduleTimestamp,
        mediaUrls: s3Urls,
      }

      // Step 3: Send scheduling request to backend
      await axios.post(
        `${config.backendUrl}/instagram/schedule-instagram-post/${activeOrganization.id}/${activeOrganization.InstagramIntegrations[0].id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
          },
        },
      )

      // Step 4: Success handling
      setSuccess("📅 Instagram post scheduled successfully.")
      setScheduleModal(false)
      setPostContent("")
      setEditorState(EditorState.createEmpty())
      setImages([])
      setImagePreviews([])
    } catch (err) {
      console.error("Error scheduling post:", err)
      setErr("Error while scheduling post. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const toolbarOptions = {
    options: ["inline", "list", "emoji"],
    inline: { options: ["bold", "italic"] },
    list: { options: ["unordered", "ordered"] },
    emoji: {},
  }

  return (
    <>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      <Card className="br" style={{ minHeight: "350px" }}>
        {loading ? (
          <div
            style={{
              minHeight: "350px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <div>
            <CardBody>
              <Row className="mb-2">
                <Col>
                  <h4
                    style={{
                      color: mode === "dark" ? "white" : "#2990DF",
                    }}
                    className="card-title text-uppercase"
                  >
                    Create Instagram Post
                  </h4>
                </Col>
              </Row>

              <Row className="px-2 mb-2">
                <Col className="text-right">
                  <Button
                    size="sm"
                    style={{
                      backgroundColor: "transparent",
                      border: "1px solid #2990DF",
                      color: "#2990DF",
                    }}
                    onClick={() => {
                      setCaptionSuggestions([])
                      toggleSuggestModal()
                    }}
                  >
                    Suggest with AI
                  </Button>
                </Col>
              </Row>

              <Row className="px-2">
                <Col>
                  <FormGroup>
                    <Editor
                      editorState={editorState}
                      toolbarClassName="editor-toolbar"
                      wrapperClassName="editor-wrapper"
                      editorClassName="editor-editor"
                      onEditorStateChange={onEditorStateChange}
                      toolbar={toolbarOptions}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="px-2 mt-1">
                <Col>
                  <div className="d-flex flex-wrap">
                    {imagePreviews.map((preview, index) => (
                      <div
                        key={index}
                        style={{ position: "relative", margin: "5px" }}
                      >
                        <img
                          src={preview}
                          alt={`preview-${index}`}
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "10px",
                          }}
                        />
                        <Button
                          color="danger"
                          size="sm"
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            borderRadius: "50%",
                          }}
                          onClick={() => removeImage(index)}
                        >
                          <span className="mdi mdi-trash-can"></span>
                        </Button>
                      </div>
                    ))}
                  </div>
                  <FormGroup>
                    <Label for="imageUpload">Upload Images</Label>
                    <Input
                      type="file"
                      id="imageUpload"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {userData.role !== "viewer" && (
                <Row className="mt-3">
                  <Col className="text-right">
                    <Button
                      style={{
                        backgroundColor: "#2990DF",
                        border: "none",
                      }}
                      size="md"
                      onClick={toggleScheduleModal}
                      disabled={postContent === "" && images.length === 0}
                    >
                      Post to Instagram
                    </Button>
                  </Col>
                </Row>
              )}
            </CardBody>
          </div>
        )}
      </Card>

      <Modal isOpen={scheduleModal} toggle={toggleScheduleModal} centered>
        <ModalHeader toggle={toggleScheduleModal}>
          Schedule or Post Now
        </ModalHeader>
        <ModalBody>
          <FormGroup className="d-flex justify-content-between">
            <Button
              style={{
                backgroundColor: postNow ? "#2990DF" : "#f0f0f0",
                border: "#2990DF",
                color: postNow ? "#fff" : "#000",
              }}
              onClick={() => {
                setPostNow(true)
                setScheduleDate("")
              }}
            >
              📢 Instant Post
            </Button>
            <Button
              style={{
                backgroundColor: !postNow ? "#2990DF" : "#f0f0f0",
                border: "#2990DF",
                color: !postNow ? "#fff" : "#000",
              }}
              onClick={() => setPostNow(false)}
            >
              ⏳ Schedule Post
            </Button>
          </FormGroup>

          {!postNow && (
            <FormGroup>
              <Label for="scheduleDate">Select Schedule Date</Label>
              <Input
                type="datetime-local"
                id="scheduleDate"
                value={scheduleDate}
                onChange={e => setScheduleDate(e.target.value)}
                placeholder="Select Date & Time"
              />
            </FormGroup>
          )}

          {activeOrganization.FacebookIntegrations[0] && (
            <FormGroup className="mt-3">
              <Label check className="d-flex align-items-center">
                <div
                  className={`custom-checkbox ${postToFacebook ? "checked" : ""}`}
                  onClick={() => setPostToFacebook(!postToFacebook)}
                >
                  {postToFacebook && <span className="checkmark">✔</span>}
                </div>
                <span className="ml-2">Post to Facebook</span>
              </Label>
            </FormGroup>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            style={{
              backgroundColor: "#2990DF",
              border: "none",
            }}
            onClick={postNow ? handlePostCreation : schedulePost}
            disabled={!postNow && !scheduleDate}
          >
            {postNow ? "Post Now" : "Schedule"}
          </Button>
          <Button onClick={toggleScheduleModal}>Cancel</Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={suggestModalOpen} toggle={toggleSuggestModal} centered>
        <ModalHeader toggle={toggleSuggestModal}>
          Suggest a Caption with AI
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label for="captionTopic">Topic (optional)</Label>
            <Input
              id="captionTopic"
              type="text"
              placeholder="e.g. new patient offer, holiday hours"
              value={captionTopic}
              onChange={e => setCaptionTopic(e.target.value)}
            />
          </FormGroup>
          <Button
            onClick={suggestCaptions}
            disabled={captionLoading}
            style={{ backgroundColor: "#2990DF", border: "none" }}
          >
            {captionLoading ? "Generating..." : "Generate Captions"}
          </Button>

          {captionSuggestions.length > 0 && (
            <div className="mt-3">
              {captionSuggestions.map((caption, index) => (
                <Card
                  key={index}
                  className="mb-2"
                  style={{ cursor: "pointer" }}
                  onClick={() => applyCaption(caption)}
                >
                  <CardBody>{caption}</CardBody>
                </Card>
              ))}
              <p className="text-muted small mb-0">
                Click a caption to load it into the editor — you can still edit it before posting.
              </p>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={toggleSuggestModal}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CreatePost
